import { v4 as uuid } from 'uuid'

import BlockId from '@/Helpers/Id/BlockId'
import { blockTypes, defaultBlocks } from '@/Helpers/constants'
import { getFocusColor, getHoverColor } from '@/Helpers/colors'

export const match = (key, values) => values[key] || values['default']

export const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
}

export const stripHtml = (string = '') => string.replace(/(<([^>]+)>)/gi, '')

export const conditionalProp = (key, prop) => {
    if (!prop) {
        return {}
    }

    return {
        [key]: prop,
    }
}

export const generateRandomString = length => {
    let result = ''
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (var i = 0; i < length; i++) {
        result += characters.charAt(
            Math.floor(Math.random() * charactersLength)
        )
    }
    return result
}

export const getBlockDisplayName = type => {
    if (type === 'paragraph') {
        return 'Paragraph'
    }

    return (blockTypes.find(block => block.type === type) || {}).label
}

export const createDefaultBlock = (type, overwrites = {}) => ({
    id: BlockId.generate(type),
    ...match(type, defaultBlocks),
    ...overwrites,
})

export const createDefaultPage = (overwrites = {}) => ({
    id: uuid(),
    slug: generateRandomString(6),
    button: 'Continue',
    blocks: [createDefaultBlock('h1')],
    ...overwrites,
})

export const createDefaultRedirectOutcome = (overwrites = {}) => ({
    id: uuid(),
    type: 'redirect',
    name: 'Redirect',
    slug: 'default',
    url: '',
    ...overwrites,
})

export const createDefaultThankYouPageOutcome = (index, overwrites = {}) => ({
    id: uuid(),
    type: 'thank_you_page',
    name: `Thank you page ${index + 1}`,
    slug: `thank-you-page-${index + 1}`,
    blocks: [createDefaultBlock('h1')],
    ...overwrites,
})

export const createDefaultForm = (overwrites = {}) => {
    const outcomeId = uuid()
    return {
        id: uuid(),
        status: 'draft',
        name: 'Untitled form',
        slug: 'untitled-form',
        default_outcome: outcomeId,
        outcomes: [
            {
                id: outcomeId,
                type: 'thank_you_page',
                name: 'Thank you page 1',
                slug: 'thank-you-page-1',
                blocks: [
                    {
                        id: BlockId.generate('h1'),
                        type: 'h1',
                        content: 'All done. Thank you!',
                    },
                    {
                        id: BlockId.generate('rich-text'),
                        type: 'rich-text',
                        content:
                            '<p>Your answers have successfully been recorded.</p>',
                    },
                    {
                        id: BlockId.generate('rich-text'),
                        type: 'rich-text',
                        content:
                            '<p>Thank you for taking the time to fill out our form!</p>',
                    },
                ],
            },
        ],
        thank_you_page: {
            heading: 'All done. Thank you!',
            body: 'Your answers have successfully been recorded.\n\nThank you for taking the time to fill out our form!',
            blocks: [
                {
                    id: BlockId.generate('h1'),
                    type: 'h1',
                    content: 'All done. Thank you!',
                },
                {
                    id: BlockId.generate('rich-text'),
                    type: 'rich-text',
                    content:
                        '<p>Your answers have successfully been recorded.</p>',
                },
                {
                    id: BlockId.generate('rich-text'),
                    type: 'rich-text',
                    content:
                        '<p>Thank you for taking the time to fill out our form!</p>',
                },
            ],
            redirect: false,
        },
        closed_page: {
            blocks: [
                {
                    id: BlockId.generate('h1'),
                    type: 'h1',
                    content: 'Sorry, we’re closed!',
                },
                {
                    id: BlockId.generate('rich-text'),
                    type: 'rich-text',
                    content:
                        '<p>This form is no longer open to new entries.</p>',
                },
            ],
        },
        branding: {},
        pages: [
            createDefaultPage({
                button: 'Send answers',
            }),
        ],
        meta: {
            source: 'form_builder',
        },
        ...overwrites,
    }
}

export const mergeRefs = (...refs) => {
    const filteredRefs = refs.filter(Boolean)

    if (!filteredRefs.length) {
        return null
    }

    if (filteredRefs.length === 1) {
        return filteredRefs[0]
    }

    return instance => {
        for (const ref of filteredRefs) {
            if (typeof ref === 'function') {
                ref(instance)
            } else if (ref) {
                ref.current = instance
            }
        }
    }
}

export const brandingToTheme = branding => {
    return {
        '--theme-primary': branding.primary_color,
        '--theme-border-active': 'var(--theme-primary)',
        '--theme-ring-default': getFocusColor(branding.primary_color),
        '--theme-button-default': 'var(--theme-primary)',
        '--theme-button-hover': getHoverColor(branding.primary_color),
        '--theme-button-text': branding?.button?.color,
    }
}

export const brandingToThemeWithoutStates = branding => {
    return {
        ...brandingToTheme(branding),
        '--theme-button-hover': 'var(--theme-primary)',
    }
}

export const removeEntriesWithEmptyValues = (values = {}) =>
    Object.fromEntries(
        Object.entries(values).filter(([_, value]) => value !== '')
    )

export const mergeObjects = (...objects) => Object.assign({}, ...objects)

export const inRange = (x, min, max) => {
    return x >= min && x <= max
}

export const overrideExistingAnswersForChangedBlocks = (
    blocks = [],
    answers = {}
) => {
    const alteredblocks = blocks
        .filter(block => answers.hasOwnProperty(block.id))
        .filter(block => {
            const answer = answers[block.id]

            if (block.type === 'checkboxes' && !Array.isArray(answer)) {
                return true
            }

            // Fix issue where some checkbox answers were nested in deep arrays
            if (
                block.type === 'checkboxes' &&
                Array.isArray(answer) &&
                answer.length > 0 &&
                Array.isArray(answer[0])
            ) {
                return true
            }

            if (block.type === 'confirm' && typeof answer !== 'boolean') {
                return true
            }

            if (
                block.type === 'file-upload' &&
                (Array.isArray(answer) || typeof answer === 'string')
            ) {
                return true
            }

            if (
                !['checkboxes', 'file-upload', 'confirm'].includes(
                    block.type
                ) &&
                typeof answer !== 'string' &&
                typeof answer !== 'number'
            ) {
                return true
            }

            if (
                block.type === 'radios' &&
                !block.hasCustomOption &&
                !block.options.map(option => option.value).includes(answer)
            ) {
                return true
            }

            return false
        })

    const newAnswers = Object.fromEntries(
        alteredblocks.map(block => {
            const answer = answers[block.id]

            if (
                !['checkboxes', 'file-upload', 'confirm'].includes(block.type)
            ) {
                return [block.id, '']
            }

            if (block.type === 'checkboxes') {
                return [block.id, []]
            }

            if (block.type === 'confirm') {
                return [block.id, false]
            }

            if (block.type === 'file-upload') {
                return [
                    block.id,
                    {
                        id: '',
                        type: '',
                        name: '',
                    },
                ]
            }

            return [block.id, answer]
        })
    )

    return newAnswers
}

export const sendMessage = message => {
    const event = new CustomEvent('reform:message', {
        detail: {
            message,
        },
    })

    window.dispatchEvent(event)
}

export const isObject = obj => {
    return Object.prototype.toString.call(obj) === '[object Object]'
}

export const hasFeature = (features, feature) =>
    features.some(item => item === feature)

export const getRandomArrayItem = array =>
    array[Math.floor(Math.random() * array.length)]

export const range = (length, start) =>
    [...Array(length).keys()].map(i => i + start)

export const getCookie = name => {
    const cookie = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => {
            return cookie.startsWith(`${name}=`)
        })

    if (!cookie) {
        return null
    }

    return cookie.replace(`${name}=`, '')
}

export const containsDuplicates = array => {
    return array.some(element => {
        if (array.indexOf(element) !== array.lastIndexOf(element)) {
            return true
        }

        return false
    })
}

export const unique = array => {
    return [...new Set(array)]
}

export const copyToClipboard = (content, message) => {
    navigator.clipboard.writeText(content)

    if (message) {
        sendMessage(message)
    }
}

export function record(event, context = {}) {
    window.dataLayer = window.dataLayer || []

    window.dataLayer.push({ ...context, event })
}

export function trackAnalyticsEvent(name, context) {
    const computedContext = {
        visitor_id: getCookie('ga4_visitor_id'),
        timestamp: Date.now(),
        ...context,
    }

    if (!window.zaraz) {
        console.log('Tracking', name, computedContext)
        return
    }

    window.zaraz.track(name, computedContext)
}

export function getDefaultCss() {
    return `/* Target block types */
.block-heading-1 {}

/* Target elements within a block */
.block-short-text label {}

/* Target specific pages */
#page-thank-you {}

/* See more options:  */
/* https://docs.reform.app/article/25-custom-css */
    `
}
